import { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

// The dark toast at the bottom of the screen, with Undo: the tracker's stage
// moves and New roles' Save / Applied / Not for me. `choices`: a row of small
// buttons under it (why a role was hidden), and it stays a little longer.
// It waits while the pointer is on it (or a button in it has focus).

export type ToastChoice = { label: string; pick: () => void };
export type Toast = {
  key: number;
  title: string;
  sub?: string;
  undo?: () => void;
  ask?: string;
  choices?: ToastChoice[];
  /** true: the pointer is on it, so it stays; false: it left, so it goes after a moment */
  hold?: (on: boolean) => void;
};

/** After the pointer leaves the toast */
const LINGER_MS = 3000;

/** The toast on screen and `show` / `hide` for it. A new one replaces the last; each goes after 5 s (8 s with choices). */
export function useUndoToast() {
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<number>();
  const show = useCallback((t: Omit<Toast, "key" | "hold">) => {
    const goAfter = (ms: number) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setToast(null), ms);
    };
    const hold = (on: boolean) => (on ? window.clearTimeout(timer.current) : goAfter(LINGER_MS));
    setToast({ ...t, key: Date.now(), hold });
    goAfter(t.choices?.length ? 8000 : 5000);
  }, []);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setToast(null);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return { toast, show, hide };
}

export default function UndoToast({ toast }: { toast: Toast | null }) {
  if (!toast) return null;
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-6 z-[70] flex justify-center">
      <div
        key={toast.key}
        role="status"
        onMouseEnter={() => toast.hold?.(true)}
        onMouseLeave={() => toast.hold?.(false)}
        onFocus={() => toast.hold?.(true)}
        onBlur={() => toast.hold?.(false)}
        className="pointer-events-auto min-w-0 rounded-[13px] bg-melt-text1 py-2.5 pl-3 pr-2.5 font-grotesk text-[#fbe7cf] shadow-[0_20px_40px_-20px_rgba(92,52,23,.7)] animate-in fade-in-0 slide-in-from-bottom-3 duration-300"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-melt-caramel text-white">
            <Check size={14} strokeWidth={2.25} />
          </span>
          <div className="min-w-0">
            <b className="block truncate text-[13.5px] font-semibold text-[#fffdf9]">{toast.title}</b>
            {toast.sub && <span className="block truncate text-[12.5px]">{toast.sub}</span>}
          </div>
          {toast.undo && (
            <button
              type="button"
              onClick={toast.undo}
              className="flex-none rounded-lg bg-[#fffdf9]/[.12] px-[11px] py-[7px] text-[12.5px] font-semibold text-[#fffdf9] transition-colors hover:bg-[#fffdf9]/[.2]"
            >
              Undo
            </button>
          )}
        </div>
        {!!toast.choices?.length && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-9">
            {toast.ask && <span className="mr-0.5 text-[12px]">{toast.ask}</span>}
            {toast.choices.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={c.pick}
                className="rounded-lg border border-[#fffdf9]/[.18] px-2.5 py-1 text-[12px] font-medium text-[#fffdf9] transition-colors hover:bg-[#fffdf9]/[.12]"
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
